/**
 * Related sections — the ONE smart block that returned (Josh 2026-10-02,
 * docs/MODULES_VISION.md §5). Every other smart kind stays cut.
 *
 * In a master spec (or variant) the block is a placeholder. Once the spec is
 * in a project it lists the project's OTHER sections, and the writer picks
 * which ones from a checklist. The pick lives on the block itself:
 *
 *   smartParams.seeded     — the checklist has been initialised
 *   smartParams.seededFor  — the project spec the picks were made in (its own
 *                            id — see `seededForKey`)
 *   smartParams.includeIds — project-spec ids switched ON
 *   smartParams.knownIds   — ids the writer has already seen in the picker
 *
 * The default rule (owner's words: "default is turned off for specs added
 * after the fact"): sections already in the project when this spec arrives
 * start ON; sections added to the project later appear OFF. That is what
 * `seeded` buys — an unseeded block is seeded once, with everything then in
 * the project, and from then on only an explicit tick turns a section on.
 *
 * Scope (fix-round, 2026-10-02): picks belong to ONE project spec. A block
 * copied into a different project spec — another project entirely, or a
 * master/variant/module that later lands somewhere — carries `seededFor`
 * from where it was seeded, which will not match the spec it now lives in.
 * Every function here checks `seeded && seededFor === key`; anything else
 * (missing `seededFor`, or a mismatch) is treated as UNSEEDED, everywhere:
 * the virtual default lists, exactly what an unseeded block prints, until a
 * genuine edit re-seeds it for its new home.
 *
 * Pure and dependency-free like the rest of `shared/`: the export resolver,
 * the Designer's node view and the add-to-project write path all call the
 * SAME functions, so what the editor shows is what the document prints.
 */

import type { ContentBlock } from './spec';

export const RELATED_SECTIONS_KIND = 'related-sections';

/** A project spec, reduced to what this block reads. */
export interface SectionRef {
  id: string;
  csiNumber?: string;
  title?: string;
  /** Archived project specs are not part of the bound book — never listed. */
  archivedAt?: string | null;
}

/** The block's parameters. The first four are the picker's; the rest are
 *  legacy keys a pre-cut block may still carry and are honoured as before. */
export interface RelatedSectionsParams {
  seeded?: boolean;
  /** The project spec these picks were made in — see `seededForKey`. */
  seededFor?: string;
  includeIds?: string[];
  knownIds?: string[];
  /** Legacy (pre-cut) exclusion list — honoured only while unseeded. */
  excludeIds?: string[];
  /** Legacy division filter — honoured only while unseeded. */
  divisionFilter?: string | null;
  /** Legacy line format. Default 'csi-and-title'. */
  format?: 'csi-and-title' | 'csi-only' | 'title-only';
}

const strings = (v: unknown): string[] | undefined =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined;

/** Read a block's raw `smartParams` bag into the typed shape. Unknown keys are
 *  left on the block untouched — this is a view, never a rewrite. */
export function readRelatedParams(raw: unknown): RelatedSectionsParams {
  const p =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const format = p.format;
  return {
    seeded: p.seeded === true,
    seededFor: typeof p.seededFor === 'string' ? p.seededFor : undefined,
    includeIds: strings(p.includeIds),
    knownIds: strings(p.knownIds),
    excludeIds: strings(p.excludeIds),
    divisionFilter: typeof p.divisionFilter === 'string' ? p.divisionFilter : null,
    format:
      format === 'csi-only' || format === 'title-only' || format === 'csi-and-title'
        ? format
        : undefined,
  };
}

/** Is this block's seed valid for the project spec it is in right now? A
 *  missing or mismatched `seededFor` means no — the picks belong elsewhere
 *  (or nowhere yet) and the block reads as unseeded. */
export function isSeededFor(params: RelatedSectionsParams, key: string): boolean {
  return params.seeded === true && !!key && params.seededFor === key;
}

/** The key picks are scoped to: a project spec's own id when it is already
 *  known (every current caller knows it), else a stable pair that still
 *  identifies exactly one row per project (one master compiles to exactly
 *  one spec row per project). */
export function seededForKey(spec: {
  id?: string | null;
  projectId?: string | null;
  masterId?: string | null;
}): string {
  if (spec.id) return spec.id;
  if (spec.projectId && spec.masterId) return `${spec.projectId}:${spec.masterId}`;
  return '';
}

export function isRelatedSectionsBlock(
  b: Pick<ContentBlock, 'blockType' | 'smartBlockKind'>
): boolean {
  return b.blockType === 'smart-token' && b.smartBlockKind === RELATED_SECTIONS_KIND;
}

/** Binding order — by section number, title breaking ties. */
function bySectionNumber(a: SectionRef, b: SectionRef): number {
  const byNum = (a.csiNumber || '').localeCompare(b.csiNumber || '');
  return byNum !== 0 ? byNum : (a.title || '').localeCompare(b.title || '');
}

/** The sections this block can list: every OTHER live spec in the project,
 *  in binding order. Never the spec itself, never an archived one. */
export function relatedCandidates(thisSpecId: string, projectSpecs: SectionRef[]): SectionRef[] {
  return projectSpecs
    .filter((s) => s.id !== thisSpecId && !s.archivedAt)
    .slice()
    .sort(bySectionNumber);
}

/** What the block lists right now, from its params and its candidates.
 *  Seeded FOR THIS SPEC → exactly the ticked ids that are still in the
 *  project. Unseeded (never seeded, or seeded somewhere else) → the virtual
 *  default: everything, minus the legacy exclusions and division filter —
 *  exactly what export prints for a block that has not been seeded yet. */
export function selectRelatedSections(
  params: RelatedSectionsParams,
  candidates: SectionRef[],
  key: string
): SectionRef[] {
  if (isSeededFor(params, key)) {
    const on = new Set(params.includeIds || []);
    return candidates.filter((s) => on.has(s.id));
  }
  const off = new Set(params.excludeIds || []);
  const div = params.divisionFilter;
  return candidates.filter((s) => !off.has(s.id) && (!div || (s.csiNumber || '').startsWith(div)));
}

/** One printed line. */
export function formatRelatedSection(
  s: SectionRef,
  format: RelatedSectionsParams['format'] = 'csi-and-title'
): string {
  const csi = s.csiNumber || '';
  const title = s.title || '(untitled)';
  if (format === 'csi-only') return csi;
  if (format === 'title-only') return title;
  return csi ? `${csi} — ${title}` : title;
}

/** The printed lines, in order — what the editor shows and the export emits. */
export function relatedSectionLines(
  params: RelatedSectionsParams,
  candidates: SectionRef[],
  key: string
): string[] {
  return selectRelatedSections(params, candidates, key).map((s) =>
    formatRelatedSection(s, params.format)
  );
}

/** How many candidates the writer has not seen in the picker yet. A block
 *  that is not seeded FOR THIS SPEC has seen nothing worth flagging — it is
 *  about to be (re)seeded, not "new". */
export function newRelatedCount(
  params: RelatedSectionsParams,
  candidates: SectionRef[],
  key: string
): number {
  if (!isSeededFor(params, key)) return 0;
  const known = new Set(params.knownIds || []);
  return candidates.filter((s) => !known.has(s.id)).length;
}

/** Merge typed params back over the raw bag, so legacy keys ride along. */
function writeParams(
  raw: unknown,
  next: Pick<RelatedSectionsParams, 'seeded' | 'seededFor' | 'includeIds' | 'knownIds'>
): Record<string, unknown> {
  const base =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return { ...base, ...next };
}

/**
 * Seed a block for THIS project spec: everything it lists right now (the
 * virtual default — ignoring any stale pick made for a different spec) is
 * switched ON and marked seen, and `seededFor` is stamped with `key`. For a
 * new block that is every candidate; for a legacy unseeded block it is
 * exactly what it already printed, so seeding never changes the output.
 * Already seeded for `key` → returned unchanged (same reference).
 */
export function seedRelatedParams(
  raw: unknown,
  candidates: SectionRef[],
  key: string
): Record<string, unknown> {
  const p = readRelatedParams(raw);
  if (isSeededFor(p, key)) return raw as Record<string, unknown>;
  return writeParams(raw, {
    seeded: true,
    seededFor: key,
    includeIds: selectRelatedSections(p, candidates, key).map((s) => s.id),
    knownIds: candidates.map((s) => s.id),
  });
}

/** Switch one section on or off (seeding first if the block never was, or was
 *  seeded for a different spec). */
export function toggleRelatedSection(
  raw: unknown,
  candidates: SectionRef[],
  key: string,
  id: string,
  on: boolean
): Record<string, unknown> {
  const seeded = seedRelatedParams(raw, candidates, key);
  const p = readRelatedParams(seeded);
  const include = (p.includeIds || []).filter((x) => x !== id);
  if (on) include.push(id);
  return writeParams(seeded, {
    seeded: true,
    seededFor: key,
    includeIds: include,
    knownIds: p.knownIds || [],
  });
}

/** Include all / Exclude all. Ids that are not candidates right now (an
 *  archived section, say) keep whatever state they had. */
export function setAllRelatedSections(
  raw: unknown,
  candidates: SectionRef[],
  key: string,
  on: boolean
): Record<string, unknown> {
  const seeded = seedRelatedParams(raw, candidates, key);
  const p = readRelatedParams(seeded);
  const ids = new Set(candidates.map((s) => s.id));
  const keep = (p.includeIds || []).filter((x) => !ids.has(x));
  return writeParams(seeded, {
    seeded: true,
    seededFor: key,
    includeIds: on ? [...keep, ...candidates.map((s) => s.id)] : keep,
    knownIds: p.knownIds || [],
  });
}

/** The picker was opened on a block seeded for THIS spec: every current
 *  candidate is now "seen". A block that is not seeded for this spec writes
 *  nothing here — opening the picker is not what seeds it (a pick is).
 *  Returns the same reference when there is nothing to write, so callers can
 *  skip a no-op save. */
export function markRelatedSeen(
  raw: unknown,
  candidates: SectionRef[],
  key: string
): Record<string, unknown> {
  const p = readRelatedParams(raw);
  if (!isSeededFor(p, key) || newRelatedCount(p, candidates, key) === 0) {
    return raw as Record<string, unknown>;
  }
  const known = new Set(p.knownIds || []);
  for (const s of candidates) known.add(s.id);
  return writeParams(raw, {
    seeded: true,
    seededFor: key,
    includeIds: p.includeIds || [],
    knownIds: [...known],
  });
}

/** Seed every related-sections block in a list that is not already seeded
 *  for `key` (never seeded, or seeded for a different spec). Returns the
 *  same array when nothing needed seeding. */
export function seedRelatedBlocks(
  blocks: ContentBlock[],
  candidates: SectionRef[],
  key: string
): ContentBlock[] {
  let changed = false;
  const out = blocks.map((b) => {
    if (!isRelatedSectionsBlock(b)) return b;
    const next = seedRelatedParams(b.smartParams, candidates, key);
    if (next === b.smartParams) return b;
    changed = true;
    return { ...b, smartParams: next };
  });
  return changed ? out : blocks;
}

/** The part of a project spec that carries blocks. */
export interface SeedableSpec extends SectionRef {
  coreContent?: ContentBlock[];
  systems?: Array<{ content?: ContentBlock[] }>;
}

/**
 * Hook 3a — specs were just added to a project. Called ONCE for the whole add
 * (a multi-spec add is one moment), with the project's full membership after
 * it: every unseeded block in a NEWCOMER is seeded with every other section
 * then in the project, batch-mates included. Specs that were already there
 * are not passed in and are never touched — the newcomer is simply absent
 * from their `includeIds`, so it shows up in their picker switched OFF.
 */
export function seedAddedSpecs<T extends SeedableSpec>(
  added: T[],
  projectSpecsAfter: SectionRef[]
): T[] {
  return added.map((spec) => {
    const key = seededForKey(spec);
    const candidates = relatedCandidates(spec.id, projectSpecsAfter);
    const coreIn = spec.coreContent || [];
    const core = seedRelatedBlocks(coreIn, candidates, key);
    let systemsChanged = false;
    const systems = (spec.systems || []).map((sys) => {
      const contentIn = sys.content || [];
      const content = seedRelatedBlocks(contentIn, candidates, key);
      if (content === contentIn) return sys;
      systemsChanged = true;
      return { ...sys, content };
    });
    if (core === coreIn && !systemsChanged) return spec;
    return {
      ...spec,
      ...(core !== coreIn ? { coreContent: core } : {}),
      ...(systemsChanged ? { systems } : {}),
    };
  });
}
